import React from "react";
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { formatMoneda, obtenerImpuestosFactura, obtenerTotalesOtrosImpuestosFactura } from "./utils";
import ImpuestosFacturaPanel from "./ImpuestosFacturaPanel";

const FilaLinea = ({ descripcion, importe, detalle, destacado = false }) => (
  <TableRow hover={!destacado}>
    <TableCell sx={{ py: 0.75, borderBottom: "1px solid #eee" }}>
      <Typography
        variant="body2"
        fontWeight={destacado ? 600 : 400}
        color={destacado ? "primary.main" : "text.primary"}
      >
        {descripcion}
      </Typography>
      {detalle ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {detalle}
        </Typography>
      ) : null}
    </TableCell>
    <TableCell
      align="right"
      sx={{
        py: 0.75,
        borderBottom: "1px solid #eee",
        fontWeight: destacado ? 700 : 600,
        whiteSpace: "nowrap",
        color: destacado ? "primary.main" : "text.primary",
      }}
    >
      {formatMoneda(importe)}
    </TableCell>
  </TableRow>
);

const subtituloLineaRetencion = (linea) => {
  if (linea.tipo !== "retencion") return null;
  const partes = [];
  if (linea.alicuota != null && linea.base != null) {
    partes.push(`${linea.alicuota}% sobre ${formatMoneda(linea.base)}`);
  } else if (linea.base != null) {
    partes.push(`Base: ${formatMoneda(linea.base)}`);
  }
  if (linea.provincia) {
    partes.push(linea.provincia);
  }
  return partes.length > 0 ? partes.join(" · ") : null;
};

const FilaResumen = ({ label, valor, destacado = false, negativo = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      py: 0.5,
    }}
  >
    <Typography
      variant="body2"
      color={destacado ? "primary.main" : "text.secondary"}
      fontWeight={destacado ? 600 : 400}
    >
      {label}
    </Typography>
    <Typography
      variant={destacado ? "subtitle1" : "body2"}
      fontWeight={destacado ? 700 : 600}
      color={
        destacado ? "primary.main" : negativo ? "error.main" : "text.primary"
      }
    >
      {negativo && valor !== "—" ? `− ${valor}` : valor}
    </Typography>
  </Box>
);

/**
 * Layout de dos columnas alineado con el PDF de la OP
 * (valores enviados | comprobantes imputados + resumen).
 */
const DetalleOrdenPagoDocumento = ({ detalle }) => {
  if (!detalle) return null;

  const {
    titulo_valores = "Enviamos los siguientes valores",
    titulo_comprobantes = "Para aplicar a los comprobantes",
    lineas_valores = [],
    comprobantes = [],
    resumen = {},
  } = detalle;

  const impuestosFactura = obtenerImpuestosFactura(null, detalle);
  const totalesOtrosImpuestos = obtenerTotalesOtrosImpuestosFactura(null, detalle);

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <Box
          sx={{
            border: "1px solid #d0dce8",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 1.5, py: 1, backgroundColor: "#2471A3" }}>
            <Typography variant="subtitle2" fontWeight={600} color="#fff">
              {titulo_valores}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {lineas_valores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">
                        Sin valores
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  lineas_valores.map((linea, index) => (
                    <FilaLinea
                      key={`${linea.tipo}-${linea.impuesto ?? "pago"}-${index}`}
                      descripcion={linea.descripcion}
                      importe={linea.importe}
                      detalle={subtituloLineaRetencion(linea)}
                      destacado={linea.tipo === "pago"}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box
          sx={{
            border: "1px solid #d0dce8",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 1.5, py: 1, backgroundColor: "#2471A3" }}>
            <Typography variant="subtitle2" fontWeight={600} color="#fff">
              {titulo_comprobantes}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {comprobantes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">
                        Sin comprobantes
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  comprobantes.map((comp, index) => (
                    <FilaLinea
                      key={comp.nro_comprobante ?? comp.descripcion ?? index}
                      descripcion={comp.descripcion}
                      importe={comp.importe}
                      detalle={comp.tipo_pago_label || null}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      <Box sx={{ maxWidth: 420, ml: { md: "auto" }, px: { xs: 0, md: 1 } }}>
        <FilaResumen
          label="Total comprobantes"
          valor={formatMoneda(resumen.total_comprobantes)}
        />
        {(resumen.total_retenciones_ganancias ?? 0) > 0 && (
          <FilaResumen
            label="(−) Retención Ganancias"
            valor={formatMoneda(resumen.total_retenciones_ganancias)}
            negativo
          />
        )}
        {(resumen.total_retenciones_iva ?? 0) > 0 && (
          <FilaResumen
            label="(−) Retención IVA"
            valor={formatMoneda(resumen.total_retenciones_iva)}
            negativo
          />
        )}
        {(resumen.total_retenciones_iibb ?? 0) > 0 && (
          <FilaResumen
            label="(−) Retención Ing. Brutos"
            valor={formatMoneda(resumen.total_retenciones_iibb)}
            negativo
          />
        )}
        {(resumen.total_retenciones ?? 0) > 0 &&
          !(resumen.total_retenciones_ganancias > 0) &&
          !(resumen.total_retenciones_iva > 0) &&
          !(resumen.total_retenciones_iibb > 0) && (
            <FilaResumen
              label="(−) Total retenciones"
              valor={formatMoneda(resumen.total_retenciones)}
              negativo
            />
          )}
        <Divider sx={{ my: 0.75 }} />
        <FilaResumen
          label="Total neto al proveedor"
          valor={formatMoneda(resumen.total_neto ?? resumen.total_a_pagar)}
          destacado
        />
      </Box>

      {impuestosFactura.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <ImpuestosFacturaPanel
            impuestos={impuestosFactura}
            totales={totalesOtrosImpuestos}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default DetalleOrdenPagoDocumento;
